import { KpiItem, KpiRow, OptimizePlanRequest, PlanKpiResponse, ProductionPlanService, ProductionTask_Output as ProductionTask } from '@/api'
import { useMutationTaskWithProgress } from '@/api/hooks/useTaskWithProgress'
import { Button } from 'primereact/button'
import { DataTable } from 'primereact/datatable'
import { Column } from 'primereact/column'
import { Dialog } from 'primereact/dialog'
import { Message } from 'primereact/message'
import { ProgressBar } from 'primereact/progressbar'
import { useEffect, useState } from 'react'

type PlanColumn = 'excel' | 'current' | 'other'
type EchoAlgorithm = 'v6' | 'v7'

const ALGORITHM_LABELS: Record<EchoAlgorithm, string> = {
  v6: '旧方式（V6）',
  v7: '新方式（V7）',
}

interface PlanComparisonDialogProps {
  visible: boolean
  onHide(): void
  excelTasks: ProductionTask[]
  currentTasks: ProductionTask[]
  currentKpis?: PlanKpiResponse | null
  currentAlgorithm?: EchoAlgorithm | null
  optimizeRequest: OptimizePlanRequest | null
  onSelectSku(skuId: string): void
}

interface SelectedCell {
  column: PlanColumn
  row: KpiRow
}

const formatValue = (row: KpiRow | undefined): string => {
  if (!row || row.value === null || row.value === undefined) return '―'
  const digits = Number.isInteger(row.value) ? 0 : 2
  return `${row.value.toLocaleString('ja-JP', { maximumFractionDigits: digits })} ${row.unit}`
}

const PlanComparisonDialog = ({
  visible,
  onHide,
  excelTasks,
  currentTasks,
  currentKpis,
  currentAlgorithm,
  optimizeRequest,
  onSelectSku,
}: PlanComparisonDialogProps) => {
  const [selected, setSelected] = useState<SelectedCell | null>(null)
  const days = optimizeRequest?.days ?? []
  const start = days[0]?.date
  const end = days[days.length - 1]?.date
  const otherAlgorithm: EchoAlgorithm = currentAlgorithm === 'v6' ? 'v7' : 'v6'

  const excelKpi = useMutationTaskWithProgress((tasks: ProductionTask[]) =>
    ProductionPlanService.planKpi({ productionTasks: tasks, start: start ?? '', end: end ?? '', days }),
  )
  const currentKpi = useMutationTaskWithProgress((tasks: ProductionTask[]) =>
    ProductionPlanService.planKpi({ productionTasks: tasks, start: start ?? '', end: end ?? '', days }),
  )
  const otherRun = useMutationTaskWithProgress((req: OptimizePlanRequest) => ProductionPlanService.optimize(req))

  // 開いたときに Excel 上の計画と (最適化の応答に評価表が無ければ) 今回の計画を評価する
  useEffect(() => {
    if (!visible || !start || !end) return
    if (!excelKpi.data && !excelKpi.isPending) excelKpi.mutate([excelTasks])
    if (!currentKpis && !currentKpi.data && !currentKpi.isPending) currentKpi.mutate([currentTasks])
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, start, end])

  const results: Record<PlanColumn, PlanKpiResponse | null | undefined> = {
    excel: excelKpi.data,
    current: currentKpis ?? currentKpi.data,
    other: otherRun.data?.kpis,
  }
  const pending: Record<PlanColumn, boolean> = {
    excel: excelKpi.isPending,
    current: !currentKpis && currentKpi.isPending,
    other: otherRun.isPending,
  }
  const columns: { key: PlanColumn; header: string }[] = [
    { key: 'excel', header: 'Excel 上の計画' },
    { key: 'current', header: `今回の計画${currentAlgorithm ? `（${ALGORITHM_LABELS[currentAlgorithm]}）` : ''}` },
    { key: 'other', header: ALGORITHM_LABELS[otherAlgorithm] },
  ]

  const base = results.current ?? results.excel ?? results.other
  const rows = (base?.rows ?? []).map((r) => ({ key: r.key, label: r.label, description: r.description }))

  const rowOf = (column: PlanColumn, key: string): KpiRow | undefined =>
    results[column]?.rows.find((r) => r.key === key)

  const notes = Array.from(new Set(columns.flatMap((c) => results[c.key]?.notes ?? [])))

  const runOther = () => {
    if (!optimizeRequest) return
    otherRun.mutate([{ ...optimizeRequest, optimize: OptimizePlanRequest.optimize.OPTIMIZE, algorithm: otherAlgorithm }])
  }

  const valueCell = (column: PlanColumn) => (row: { key: string }) => {
    if (pending[column]) return <i className="pi pi-spin pi-spinner text-gray-400" />
    const kpi = rowOf(column, row.key)
    if (!kpi) return <span className="text-gray-400">―</span>
    const hasItems = kpi.items.length > 0
    const isSelected = selected?.column === column && selected.row.key === row.key
    return hasItems ? (
      <Button
        link
        className={`p-0 ${isSelected ? 'font-bold' : ''}`}
        label={formatValue(kpi)}
        onClick={() => setSelected({ column, row: kpi })}
      />
    ) : (
      <span>{formatValue(kpi)}</span>
    )
  }

  const selectedHeader = selected
    ? `${selected.row.label}（${columns.find((c) => c.key === selected.column)?.header}）`
    : ''

  return (
    <Dialog header="計画の比較" visible={visible} style={{ width: '80vw' }} onHide={onHide} maximizable>
      <p className="text-sm text-600 mt-0">
        期間 {start} 〜 {end} の計画を、同じ基準で比較します。数値をクリックすると該当する品目が表示され、
        品目を選ぶとその品目の詳細に移ります。
      </p>
      {notes.map((n) => (
        <Message key={n} severity="info" text={n} className="mb-2 w-full justify-content-start" />
      ))}
      <DataTable value={rows} size="small" stripedRows dataKey="key">
        <Column
          header="指標"
          body={(row: { label: string; description: string }) => (
            <div>
              <div className="font-medium">{row.label}</div>
              <div className="text-xs text-600 mt-1">{row.description}</div>
            </div>
          )}
          style={{ width: '40%' }}
        />
        {columns.map((c) => (
          <Column
            key={c.key}
            header={
              c.key === 'other' && !otherRun.data && !otherRun.isPending ? (
                <div className="flex flex-column gap-1">
                  <span>{c.header}</span>
                  <Button
                    size="small"
                    outlined
                    label="この方式でも計算する"
                    onClick={runOther}
                    disabled={!optimizeRequest}
                  />
                </div>
              ) : (
                c.header
              )
            }
            body={valueCell(c.key)}
          />
        ))}
      </DataTable>
      {otherRun.isPending && (
        <div className="mt-3">
          <ProgressBar
            value={otherRun.progress !== null ? Math.round(Math.min(1, Math.max(0, otherRun.progress)) * 100) : undefined}
            mode={otherRun.progress !== null ? 'determinate' : 'indeterminate'}
          />
          <small className="block mt-1 text-600">
            {ALGORITHM_LABELS[otherAlgorithm]}で計算しています（旧方式は10分ほどかかります）: {otherRun.lastLog}
          </small>
        </div>
      )}
      {(excelKpi.isError || currentKpi.isError || otherRun.isError) && (
        <Message severity="error" text="評価表を作れませんでした。時間をおいてもう一度開いてください。" className="mt-3" />
      )}
      {selected && (
        <div className="mt-4">
          <h4 className="mt-0 mb-2">{selectedHeader}</h4>
          <DataTable value={selected.row.items} size="small" paginator rows={10} emptyMessage="該当なし">
            <Column
              header="品目"
              body={(item: KpiItem) => (
                <Button
                  link
                  className="p-0"
                  label={item.sku}
                  onClick={() => {
                    onSelectSku(item.sku)
                    onHide()
                  }}
                />
              )}
            />
            <Column field="machine" header="機械" />
            <Column field="date" header="日付" />
            <Column header="値" body={(item: KpiItem) => item.value?.toLocaleString('ja-JP') ?? ''} />
            <Column field="note" header="補足" />
          </DataTable>
        </div>
      )}
    </Dialog>
  )
}

export default PlanComparisonDialog
